import { useEffect, useRef } from 'react'

import { Icona } from './Icon'

/** Gli overlay aperti, dal più vecchio al più recente: Escape chiude solo l'ultimo. */
const pila = []

const FOCALIZZABILI =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'

/**
 * Comportamento comune di drawer e dialog: blocca lo scroll della pagina, porta il focus
 * nel pannello, lo tiene dentro con Tab, chiude con Escape (solo l'overlay in cima) e a
 * chiusura restituisce il focus a chi lo aveva.
 * @param {import('react').RefObject<HTMLElement>} pannelloRef - Il pannello
 * @param {() => void} onClose - Chiusura
 */
const useOverlay = (pannelloRef, onClose) => {
  const chiudiRef = useRef(onClose)
  chiudiRef.current = onClose

  useEffect(() => {
    const prima = document.activeElement
    const marca = Symbol('overlay')
    pila.push(marca)
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const pannello = pannelloRef.current
    // Il primo controllo del pannello, altrimenti il pannello stesso.
    const [primo] = pannello?.querySelectorAll(FOCALIZZABILI) ?? []
    ;(primo ?? pannello)?.focus()

    const suTasto = (evento) => {
      if (pila.at(-1) !== marca) return
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        chiudiRef.current()
        return
      }
      if (evento.key !== 'Tab' || !pannello) return
      const elementi = [...pannello.querySelectorAll(FOCALIZZABILI)].filter(
        (el) => el.offsetParent !== null
      )
      if (!elementi.length) {
        evento.preventDefault()
        return
      }
      const ultimo = elementi.at(-1)
      const inizio = elementi[0]
      if (
        evento.shiftKey &&
        (document.activeElement === inizio || document.activeElement === pannello)
      ) {
        evento.preventDefault()
        ultimo.focus()
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault()
        inizio.focus()
      }
    }
    window.addEventListener('keydown', suTasto)
    return () => {
      window.removeEventListener('keydown', suTasto)
      pila.splice(pila.indexOf(marca), 1)
      document.body.style.overflow = overflow
      if (prima instanceof HTMLElement && document.contains(prima)) prima.focus()
    }
  }, [pannelloRef])
}

/**
 * Pannello laterale da destra, sopra la pagina. Il velo dietro chiude con un clic.
 * @param {Object} props - Proprietà
 * @param {string} props.etichetta - Nome accessibile del pannello
 * @param {string} props.chiudiEtichetta - Testo accessibile del bottone di chiusura
 * @param {() => void} props.onClose - Chiusura
 * @param {boolean} [props.largo] - Pannello largo (tabelle)
 * @param {import('react').ReactNode} props.children - Contenuto
 */
export const Drawer = ({ etichetta, chiudiEtichetta, onClose, largo = false, children }) => {
  const pannelloRef = useRef(null)
  useOverlay(pannelloRef, onClose)
  return (
    <div className="ae-drawer" role="presentation">
      <button
        type="button"
        className="ae-drawer__velo"
        aria-label={chiudiEtichetta}
        tabIndex={-1}
        onClick={onClose}
      />
      <aside
        ref={pannelloRef}
        className={`ae-drawer__pannello ${largo ? 'ae-drawer__pannello--largo' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={etichetta}
        tabIndex={-1}
      >
        <div className="ae-drawer__chiudi">
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm nbtn--icon"
            aria-label={chiudiEtichetta}
            onClick={onClose}
          >
            <Icona nome="pi-times" />
          </button>
        </div>
        <div className="ae-drawer__corpo">{children}</div>
      </aside>
    </div>
  )
}

/**
 * Finestra centrata con titolo, sottotitolo e bottone di chiusura.
 * @param {Object} props - Proprietà
 * @param {string} props.titolo - Titolo
 * @param {import('react').ReactNode} [props.sottotitolo] - Riga sotto il titolo
 * @param {string} props.id - Prefisso degli id per `aria-labelledby`
 * @param {string} props.chiudiEtichetta - Testo accessibile della chiusura
 * @param {() => void} props.onClose - Chiusura
 * @param {import('react').ReactNode} props.children - Contenuto
 */
export const Dialog = ({ titolo, sottotitolo, id, chiudiEtichetta, onClose, children }) => {
  const pannelloRef = useRef(null)
  useOverlay(pannelloRef, onClose)
  return (
    <div className="ae-dialog" role="presentation">
      <button
        type="button"
        className="ae-dialog__velo"
        aria-label={chiudiEtichetta}
        tabIndex={-1}
        onClick={onClose}
      />
      <section
        ref={pannelloRef}
        className="ae-dialog__pannello"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titolo`}
        tabIndex={-1}
      >
        <div className="ae-dialog__testa">
          <div>
            <h2 id={`${id}-titolo`}>{titolo}</h2>
            {sottotitolo ? <p>{sottotitolo}</p> : null}
          </div>
          <button
            type="button"
            className="nbtn nbtn--ghost nbtn--sm nbtn--icon"
            aria-label={chiudiEtichetta}
            onClick={onClose}
          >
            <Icona nome="pi-times" />
          </button>
        </div>
        {children}
      </section>
    </div>
  )
}
